import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { FileUpload } from "primereact/fileupload";
import { InputTextarea } from "primereact/inputtextarea";
import { useRef } from "react";
import axios from "axios";
import "../styles/Agendamento.css";
import { hideProcessing, showProcessing } from "../utils/utils";
import anexoService from "../services/anexoService";

let _agendamentoInitialized = false;

export default function Agendamento() {
    const { externalAccessUuid } = useParams();
    const [agendamento, setAgendamento] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mostrarModalFinalizacao, setMostrarModalFinalizacao] = useState(false);
    const [monitoramentoAtivo, setMonitoramentoAtivo] = useState(false);
    const [usuarioMonitoramento, setUsuarioMonitoramento] = useState("");
    const [senhaMonitoramento, setSenhaMonitoramento] = useState("");
    const [carregandoFinalizacao, setCarregandoFinalizacao] = useState(false);
    const [anexos, setAnexos] = useState([]);
    const [tipoAnexo, setTipoAnexo] = useState("FOTO_ANTES");
    const [carregandoAnexo, setCarregandoAnexo] = useState(false);
    const [observacaoAnexo, setObservacaoAnexo] = useState("");
    const [observacoesTecnico, setObservacoesTecnico] = useState("");
    const [anexoSelecionado, setAnexoSelecionado] = useState(null);
    const [anexoBase64, setAnexoBase64] = useState(null);
    const [carregandoVisualizacao, setCarregandoVisualizacao] = useState(false);
    const [carregandoDelecao, setCarregandoDelecao] = useState(false);
    const [anexoParaDeletar, setAnexoParaDeletar] = useState(null);
    const toastRef = useRef(null);
    const fileUploadRef = useRef(null);
    const API_URL = process.env.REACT_APP_HOST_API;

    useEffect(() => {

        if (!_agendamentoInitialized) {
            _agendamentoInitialized = true;
            return;
        }

        carregarAgendamento();
    }, [externalAccessUuid]);

    const carregarAgendamento = async () => {
        try {
            setLoading(true);
            const url = `${API_URL}/v1/publico/agenda/external/${externalAccessUuid}`;
            const response = await axios.get(url);
            setAgendamento(response.data);
            
            // Carregar anexos se existirem
            if (response.data.anexos && response.data.anexos.length > 0) {
                setAnexos(response.data.anexos);
            }
        } catch (error) {
            console.error("Erro ao carregar agendamento:", error);
            toastRef.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Agendamento não encontrado ou erro ao carregar dados",
                life: 4000
            });
        } finally {
            setLoading(false);
        }
    };

    const iniciarAgendamento = async () => {
        try {
            await showProcessing();

            const url = `${API_URL}/v1/publico/agenda/external/${externalAccessUuid}/iniciar`;
            const response = await axios.put(url);
            setAgendamento(response.data);
            toastRef.current?.show({
                severity: "success",
                summary: "Sucesso",
                detail: "Agendamento iniciado! Você pode agora anexar fotos/vídeos.",
                life: 3000
            });
        } catch (error) {
            console.error("Erro ao iniciar agendamento:", error);
            toastRef.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Erro ao iniciar agendamento",
                life: 3000
            });
        }
        hideProcessing();
    };

    const finalizarAgendamento = async () => {
        try {
            await showProcessing();

            if (monitoramentoAtivo && (!usuarioMonitoramento || !senhaMonitoramento)) {
                toastRef.current?.show({
                    severity: "warn",
                    summary: "Atenção",
                    detail: "Informe o usuário e senha do monitoramento",
                    life: 3000
                });
                hideProcessing();
                return;
            }

            setCarregandoFinalizacao(true);
            const url = `${API_URL}/v1/publico/agenda/external/${externalAccessUuid}/finalizar`;
            const payload = {
                monitoramentoAtivo,
                usuarioMonitoramento: monitoramentoAtivo ? usuarioMonitoramento : null,
                senhaMonitoramento: monitoramentoAtivo ? senhaMonitoramento : null,
                observacoesTecnico: observacoesTecnico || null
            };

            const response = await axios.put(url, payload);
            setAgendamento(response.data);
            setMostrarModalFinalizacao(false);
            
            toastRef.current?.show({
                severity: "success",
                summary: "Sucesso",
                detail: "Agendamento finalizado com sucesso!",
                life: 3000
            });

            // Resetar campos
            setMonitoramentoAtivo(false);
            setUsuarioMonitoramento("");
            setSenhaMonitoramento("");
        } catch (error) {
            console.error("Erro ao finalizar agendamento:", error);
            toastRef.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Erro ao finalizar agendamento",
                life: 3000
            });
        } finally {
            setCarregandoFinalizacao(false);
        }

        hideProcessing();
    };

    const anexarArquivo = async (file, tipo) => {
        try {

            await showProcessing();

            setCarregandoAnexo(true);

            // Converter arquivo para base64
            const reader = new FileReader();
            reader.onload = async (event) => {
                const base64 = event.target.result.split(',')[1];

                const payload = {
                    nomeArquivo: file.name,
                    tipo: tipo,
                    base64: base64
                };

                const url = `${API_URL}/v1/publico/agenda/external/${externalAccessUuid}/anexar`;
                const response = await axios.post(url, payload);

                // Adicionar anexo à lista
                setAnexos([...anexos, response.data]);

                // Resetar campos
                setTipoAnexo("FOTO_ANTES");
                setObservacaoAnexo("");
                fileUploadRef.current?.clear();

                toastRef.current?.show({
                    severity: "success",
                    summary: "Sucesso",
                    detail: "Arquivo anexado com sucesso!",
                    life: 3000
                });
            };

            reader.readAsDataURL(file);
        } catch (error) {
            console.error("Erro ao anexar arquivo:", error);
            toastRef.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Erro ao anexar arquivo",
                life: 3000
            });
        } finally {
            setCarregandoAnexo(false);
        }

        hideProcessing();
    };

    const handleUploadArquivoAntes = (e) => {
        const file = e.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                toastRef.current?.show({
                    severity: "warn",
                    summary: "Atenção",
                    detail: "Arquivo muito grande (máximo 10MB)",
                    life: 3000
                });
                return;
            }
            anexarArquivo(file, "FOTO_ANTES");
        }
    };

    const handleUploadArquivoDepois = (e) => {
        const file = e.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                toastRef.current?.show({
                    severity: "warn",
                    summary: "Atenção",
                    detail: "Arquivo muito grande (máximo 10MB)",
                    life: 3000
                });
                return;
            }
            anexarArquivo(file, "FOTO_DEPOIS");
        }
    };

    const visualizarAnexo = async (anexo) => {
        try {
            setCarregandoVisualizacao(true);
            setAnexoSelecionado(anexo);
            
            // Se já tem base64, usar direto
            if (anexo.base64) {
                setAnexoBase64(anexo.base64);
                return;
            }
            
            // Caso contrário, buscar do servidor
            if (anexo.id) {
                const base64 = await anexoService.obterAnexoBase64(anexo.id);
                setAnexoBase64(base64);
            }
        } catch (error) {
            console.error("Erro ao visualizar anexo:", error);
            toastRef.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Erro ao carregar anexo",
                life: 3000
            });
            setAnexoSelecionado(null);
        } finally {
            setCarregandoVisualizacao(false);
        }
    };

    const deleteAnexoHandler = async (anexo) => {
        try {
            if (!anexo?.id) {
                toastRef.current?.show({
                    severity: "warn",
                    summary: "Atenção",
                    detail: "Não foi possível identificar o anexo para exclusão",
                    life: 3000
                });
                return;
            }

            setCarregandoDelecao(true);
            
            await anexoService.deleteAnexo(anexo.id);
            
            // Remover do array de anexos
            setAnexos(anexos.filter(a => a.id !== anexo.id));
            
            // Fechar dialog se estava visualizando este anexo
            if (anexoSelecionado?.id === anexo.id) {
                setAnexoSelecionado(null);
                setAnexoBase64(null);
            }
            
            toastRef.current?.show({
                severity: "success",
                summary: "Sucesso",
                detail: "Arquivo deletado com sucesso!",
                life: 3000
            });
        } catch (error) {
            console.error("Erro ao deletar anexo:", error);
            toastRef.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Erro ao deletar arquivo",
                life: 3000
            });
        } finally {
            setCarregandoDelecao(false);
        }
    };

    const confirmDeleteAnexo = (anexo) => {
        confirmDialog({
            message: `Tem certeza que deseja deletar o arquivo "${anexo.nomeArquivo}"?`,
            header: "Confirmar Exclusão",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Sim",
            rejectLabel: "Não",
            accept: () => deleteAnexoHandler(anexo),
            reject: () => {
                // Cancelado
            }
        });
    };

    const getStatusColor = (status) => {
        const colorMap = {
            "PENDENTE": "warning",
            "CONFIRMADO": "info",
            "CONCLUÍDO": "success",
            "CANCELADO": "danger",
            "EM PROGRESSO": "warning"
        };
        return colorMap[status] || "secondary";
    };

    const getStatusLabel = (status) => {
        const labelMap = {
            "PENDENTE": "Pendente",
            "CONFIRMADO": "Confirmado",
            "CONCLUÍDO": "Concluído",
            "CANCELADO": "Cancelado",
            "EM PROGRESSO": "Em Progresso"
        };
        return labelMap[status] || status;
    };

    const formatarData = (data) => {
        if (!data) return "N/A";
        const date = new Date(data);
        return date.toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        }).replace(",", " às");
    };

    if (loading) {
        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh"
            }}>
                <ProgressSpinner />
            </div>
        );
    }

    if (!agendamento) {
        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh",
                padding: "20px"
            }}>
                <Toast ref={toastRef} />
                <Card style={{ width: "100%", maxWidth: "600px" }}>
                    <div style={{
                        textAlign: "center",
                        padding: "40px 20px"
                    }}>
                        <h3>❌ Agendamento não encontrado</h3>
                        <p>Verifique se o link está correto.</p>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        }}>
            <Toast ref={toastRef} />
            <ConfirmDialog />
            
            <div style={{
                maxWidth: "800px",
                margin: "0",
                width: "100%"
            }}>
                <Card style={{
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    borderRadius: "8px"
                }}>
                    {/* Modal de Visualização de Anexo */}
                    <Dialog
                        visible={!!anexoSelecionado}
                        onHide={() => {
                            setAnexoSelecionado(null);
                            setAnexoBase64(null);
                        }}
                        header={`Visualizar ${anexoSelecionado?.tipo}`}
                        modal
                        style={{ width: "90vw", maxWidth: "800px" }}
                        maximizable
                    >
                        {anexoSelecionado && (
                            <div style={{ textAlign: "center" }}>
                                {carregandoVisualizacao ? (
                                    <div style={{ padding: "40px", textAlign: "center" }}>
                                        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
                                    </div>
                                ) : anexoBase64 ? (
                                    <>
                                        {anexoSelecionado.tipo.includes("FOTO") || anexoSelecionado.tipo.includes("VIDEO") ? (
                                            anexoSelecionado.nomeArquivo.toLowerCase().match(/\.(mp4|webm|ogg)$/) ? (
                                                <video
                                                    src={`data:video/mp4;base64,${anexoBase64}`}
                                                    style={{ maxWidth: "100%", maxHeight: "60vh", borderRadius: "4px" }}
                                                    controls
                                                />
                                            ) : (
                                                <img
                                                    src={`data:image/jpeg;base64,${anexoBase64}`}
                                                    alt={anexoSelecionado.nomeArquivo}
                                                    style={{ maxWidth: "100%", maxHeight: "60vh", borderRadius: "4px" }}
                                                />
                                            )
                                        ) : null}
                                        <div style={{ marginTop: "15px" }}>
                                            <p style={{ fontSize: "12px", color: "#666", margin: "5px 0" }}>
                                                <strong>Arquivo:</strong> {anexoSelecionado.nomeArquivo}
                                            </p>
                                            {anexoSelecionado.observacao && (
                                                <p style={{ fontSize: "12px", color: "#666", margin: "5px 0" }}>
                                                    <strong>Observação:</strong> {anexoSelecionado.observacao}
                                                </p>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <p style={{ color: "#999" }}>Não foi possível carregar o anexo</p>
                                )}
                            </div>
                        )}
                    </Dialog>

                    {/* Cabeçalho */}
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "20px"
                    }}>
                        <div>
                            <h1 style={{
                                margin: 0,
                                color: "#333",
                                fontSize: "22px"
                            }}>
                                Detalhes do Agendamento
                            </h1>
                            <p style={{
                                margin: "5px 0 0 0",
                                color: "#999",
                                fontSize: "11px"
                            }}>
                                ID: {agendamento.id}
                            </p>
                        </div>
                        <Tag
                            value={getStatusLabel(agendamento.status)}
                            severity={getStatusColor(agendamento.status)}
                            style={{ fontSize: "14px", padding: "8px 12px" }}
                        />
                    </div>

                    <Divider />

                    {/* Tipo, Data e Hora na mesma linha */}
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "20px",
                        marginBottom: "20px"
                    }}>
                        <div>
                            <h3 style={{
                                color: "#333",
                                marginBottom: "10px",
                                fontSize: "13px",
                                fontWeight: "600"
                            }}>
                                Tipo
                            </h3>
                            <p style={{
                                fontSize: "15px",
                                color: "#555",
                                margin: 0,
                                fontWeight: "500"
                            }}>
                                {agendamento.tipo}
                            </p>
                        </div>

                        <div>
                            <h3 style={{
                                color: "#333",
                                marginBottom: "10px",
                                fontSize: "13px",
                                fontWeight: "600"
                            }}>
                                📅 Data e Hora
                            </h3>
                            <p style={{
                                fontSize: "15px",
                                color: "#555",
                                margin: 0,
                                fontWeight: "500"
                            }}>
                                {formatarData(agendamento.dataCompromisso)}
                            </p>
                        </div>
                    </div>

                    <Divider />

                    {/* Informações da Equipe */}
                    {agendamento.equipe && (
                        <>
                            <div style={{ marginBottom: "20px" }}>
                                <h3 style={{
                                    color: "#333",
                                    marginBottom: "10px",
                                    fontSize: "13px",
                                    fontWeight: "600"
                                }}>
                                    👥 Equipe Responsável
                                </h3>
                                <div style={{padding: "0px 12px"}}>
                                    <p style={{
                                        fontSize: "14px",
                                        color: "#555",
                                        margin: 0
                                    }}>
                                        {agendamento.equipe.nome}
                                    </p>
                                    {agendamento.equipe.whatsapp && (
                                        <p style={{
                                            fontSize: "12px",
                                            color: "#999",
                                            margin: "5px 0 0 0"
                                        }}>
                                            📞 {agendamento.equipe.whatsapp}
                                        </p>
                                    )}
                                </div>
                                
                            </div>
                            <Divider />
                        </>
                    )}

                    {/* Informações do Cliente */}
                    {agendamento.cliente && (
                        <>
                            <div style={{ marginBottom: "20px" }}>
                                <h3 style={{
                                    color: "#333",
                                    marginBottom: "0px",
                                    fontSize: "13px",
                                    fontWeight: "600"
                                }}>
                                    👤 Cliente
                                </h3>
                                
                                {/* Grid com 2 colunas para dados do cliente */}
                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "15px",
                                    marginBottom: "20px"
                                }}>
                                    {/* Nome do Cliente - Coluna Cheia (span 2) */}
                                    <div style={{
                                        backgroundColor: "#f9f9f9",
                                        padding: "12px",
                                        borderRadius: "4px"
                                    }}>
                                        <p style={{
                                            margin: "0 0 8px 0",
                                            fontSize: "12px",
                                            color: "#666",
                                            fontWeight: "600"
                                        }}>
                                            Nome
                                        </p>
                                        <p style={{
                                            fontSize: "14px",
                                            color: "#333",
                                            margin: 0,
                                            fontWeight: "500"
                                        }}>
                                            {agendamento.cliente.nome}
                                        </p>
                                    </div>

                                    {/* Telefone Celular */}
                                    {agendamento.cliente.telefoneCelular && (
                                        <div style={{
                                            backgroundColor: "#f9f9f9",
                                            padding: "12px",
                                            borderRadius: "4px"
                                        }}>
                                            <p style={{
                                                margin: "0 0 8px 0",
                                                fontSize: "12px",
                                                color: "#666",
                                                fontWeight: "600"
                                            }}>
                                                📱 Telefone Celular
                                            </p>
                                            <p style={{
                                                fontSize: "14px",
                                                color: "#333",
                                                margin: 0
                                            }}>
                                                {agendamento.cliente.telefoneCelular}
                                            </p>
                                        </div>
                                    )}

                                    {/* Telefone Comercial */}
                                    {agendamento.cliente.telefoneComercial && (
                                        <div style={{
                                            backgroundColor: "#f9f9f9",
                                            padding: "12px",
                                            borderRadius: "4px"
                                        }}>
                                            <p style={{
                                                margin: "0 0 8px 0",
                                                fontSize: "12px",
                                                color: "#666",
                                                fontWeight: "600"
                                            }}>
                                                ☎️ Telefone Comercial
                                            </p>
                                            <p style={{
                                                fontSize: "14px",
                                                color: "#333",
                                                margin: 0
                                            }}>
                                                {agendamento.cliente.telefoneComercial}
                                            </p>
                                        </div>
                                    )}

                                    {agendamento.tipoTelhado && (
                                        <div style={{
                                            backgroundColor: "#f9f9f9",
                                            padding: "12px",
                                            borderRadius: "4px"
                                        }}>
                                            <p style={{
                                                margin: "0 0 8px 0",
                                                fontSize: "12px",
                                                color: "#666",
                                                fontWeight: "600"
                                            }}>
                                                🏠 Tipo de Telhado
                                            </p>
                                            <p style={{
                                                fontSize: "14px",
                                                color: "#333",
                                                margin: 0
                                            }}>
                                                {agendamento.tipoTelhado}
                                            </p>
                                        </div>
                                    )}

                                </div>

                                {/* Endereços - Largura Total */}
                                {agendamento.cliente.enderecos && agendamento.cliente.enderecos.length > 0 && (
                                    <div>
                                        <h4 style={{
                                            color: "#333",
                                            marginBottom: "10px",
                                            fontSize: "12px",
                                            fontWeight: "600"
                                        }}>
                                            📍 Endereço(s)
                                        </h4>
                                        {agendamento.cliente.enderecos.map((endereco, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    backgroundColor: "#f9f9f9",
                                                    padding: "12px",
                                                    borderRadius: "4px",
                                                    marginBottom: "10px",
                                                    borderLeft: "4px solid #667eea"
                                                }}
                                            >
                                                <p style={{
                                                    margin: "0 0 8px 0",
                                                    fontSize: "13px",
                                                    fontWeight: "600",
                                                    color: "#333"
                                                }}>
                                                    {endereco.logradouro}, {endereco.numero}
                                                    {endereco.complemento && ` - ${endereco.complemento}`}
                                                </p>
                                                <p style={{
                                                    margin: "5px 0",
                                                    fontSize: "12px",
                                                    color: "#666"
                                                }}>
                                                    {endereco.bairro} - {endereco.cidade}, {endereco.estado}
                                                </p>
                                                {endereco.cep && (
                                                    <p style={{
                                                        margin: "5px 0 0 0",
                                                        fontSize: "12px",
                                                        color: "#999"
                                                    }}>
                                                        CEP: {endereco.cep}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <Divider />
                        </>
                    )}

                    {/* Valor do Agendamento */}
                    {agendamento.valor && (
                        <>
                            <div style={{ marginBottom: "20px" }}>
                                <h3 style={{
                                    color: "#333",
                                    marginBottom: "15px",
                                    fontSize: "13px",
                                    fontWeight: "600"
                                }}>
                                    💰 Valor da Ordem
                                </h3>
                                
                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "15px"
                                }}>
                                    {/* Valor Total */}
                                    <div style={{
                                        backgroundColor: "#f9f9f9",
                                        padding: "0px 12px",
                                        borderRadius: "4px"
                                    }}>
                                        <p style={{
                                            fontSize: "16px",
                                            color: "#249152",
                                            margin: "0 0 8px 0",
                                            fontWeight: "600"
                                        }}>
                                            {new Intl.NumberFormat("pt-BR", {
                                                style: "currency",
                                                currency: "BRL"
                                            }).format(agendamento.valor)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <Divider />
                        </>
                    )}

                    {/* Observações */}
                    {agendamento.observacoes && (
                        <>
                            <div style={{ marginBottom: "20px" }}>
                                <h3 style={{
                                    color: "#333",
                                    marginBottom: "10px",
                                    fontSize: "13px",
                                    fontWeight: "600"
                                }}>
                                    📝 Observações
                                </h3>
                                <p style={{
                                    fontSize: "12px",
                                    color: "#666",
                                    margin: 0,
                                    lineHeight: "1.6",
                                    whiteSpace: "pre-wrap"
                                }}>
                                    {agendamento.observacoes}
                                </p>
                            </div>
                            <Divider />
                        </>
                    )}

                    {/* Informações de criação */}
                    {/*
                    <div style={{
                        fontSize: "12px",
                        color: "#999",
                        textAlign: "center",
                        marginTop: "20px"
                    }}>
                        <p style={{ margin: "5px 0" }}>
                            Criado em: {formatarData(agendamento.criadoEm)}
                        </p>
                        {agendamento.alteradoEm && (
                            <p style={{ margin: "5px 0" }}>
                                Última atualização: {formatarData(agendamento.alteradoEm)}
                            </p>
                        )}
                    </div>

                    <Divider />
                    /*/}

                    {/* Seção de Upload de Fotos/Vídeos (durante EM PROGRESSO) */}
                    {(agendamento.status === "EM PROGRESSO" || agendamento.status === "CONFIRMADO") && (
                        <>
                            <div style={{ marginBottom: "20px" }}>
                                <h3 style={{
                                    color: "#333",
                                    marginBottom: "10px",
                                    fontSize: "13px",
                                    fontWeight: "600"
                                }}>
                                    📸 Anexar Fotos/Vídeos
                                </h3>
                                {agendamento.tipo === "INSTALAÇÃO" && (
                                    <div>
                                        <label style={{
                                            display: "block",
                                            marginBottom: "5px",
                                            fontWeight: "600",
                                            color: "#333",
                                            fontSize: "13px"
                                        }}>
                                            Antes
                                        </label>
                                    </div>
                                )}

                                <div>
                                    <FileUpload
                                        mode="basic"
                                        ref={fileUploadRef}
                                        accept="image/*,video/*"
                                        maxFileSize={10000000}
                                        onSelect={handleUploadArquivoAntes}
                                        auto
                                        chooseLabel="Adicionar"
                                        disabled={carregandoAnexo}
                                        emptyTemplate={
                                            <p style={{
                                                textAlign: "center",
                                                padding: "20px",
                                                color: "#999"
                                            }}>
                                                Arraste arquivos aqui ou clique para selecionar
                                            </p>
                                        }
                                    />
                                </div>

                                {/* Lista de anexos */}
                                {anexos && anexos.length > 0 && (
                                    <div style={{
                                        backgroundColor: "#f9f9f9",
                                        padding: "15px",
                                        borderRadius: "4px",
                                        marginTop: "15px"
                                    }}>
                                        <h4 style={{
                                            color: "#333",
                                            marginTop: 0,
                                            marginBottom: "10px",
                                            fontSize: "12px"
                                        }}>
                                            ✅ Arquivos Anexados ({anexos.length})
                                        </h4>
                                        {anexos.filter(anexo => anexo.tipo === "FOTO_ANTES").map((anexo, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    padding: "8px",
                                                    backgroundColor: "#fff",
                                                    borderRadius: "4px",
                                                    marginBottom: "8px",
                                                    borderLeft: "4px solid #4CAF50",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "flex-start"
                                                }}
                                            >
                                                <div style={{ flex: 1 }}>
                                                    <p style={{
                                                        margin: "0 0 5px 0",
                                                        fontSize: "12px",
                                                        fontWeight: "600",
                                                        color: "#333"
                                                    }}>
                                                        {agendamento.tipo === "VISTORIA" && anexo.tipo === "FOTO_ANTES" && "📷 Foto VISTORIA"} 
                                                        {agendamento.tipo === "VISTORIA" && anexo.tipo === "VIDEO_ANTES" && "📷 Vídeo VISTORIA"} 
                                                        {agendamento.tipo === "INSTALAÇÃO" && anexo.tipo === "FOTO_ANTES" && "📷 Foto ANTES"}
                                                        {agendamento.tipo === "INSTALAÇÃO" && anexo.tipo === "FOTO_DEPOIS" && "📷 Foto DEPOIS"}
                                                        {agendamento.tipo === "INSTALAÇÃO" && anexo.tipo === "VIDEO_ANTES" && "🎥 Vídeo ANTES"}
                                                        {agendamento.tipo === "INSTALAÇÃO" && anexo.tipo === "VIDEO_DEPOIS" && "🎥 Vídeo DEPOIS"}
                                                        {agendamento.tipo === "INSTALAÇÃO" && anexo.tipo === "VIDEO_DEPOIS" && "🎥 Vídeo DEPOIS"}
                                                        {anexo.tipo === "OUTRO" && "📎 Outro"}
                                                    </p>
                                                    <p style={{
                                                        margin: "0 0 5px 0",
                                                        fontSize: "11px",
                                                        color: "#666"
                                                    }}>
                                                        {anexo.nomeArquivo}
                                                    </p>
                                                    {anexo.observacao && (
                                                        <p style={{
                                                            margin: "0",
                                                            fontSize: "10px",
                                                            color: "#999"
                                                        }}>
                                                            <em>{anexo.observacao}</em>
                                                        </p>
                                                    )}
                                                </div>
                                                <div style={{
                                                    display: "flex",
                                                    gap: "5px",
                                                    marginLeft: "10px"
                                                }}>
                                                    <Button
                                                        icon="pi pi-eye"
                                                        className="p-button-rounded p-button-sm p-button-outlined"
                                                        onClick={() => visualizarAnexo(anexo)}
                                                        style={{ minWidth: "32px" }}
                                                        title="Visualizar"
                                                        loading={carregandoVisualizacao}
                                                    />
                                                    <Button
                                                        icon="pi pi-trash"
                                                        className="p-button-rounded p-button-sm p-button-outlined p-button-danger"
                                                        onClick={() => confirmDeleteAnexo(anexo)}
                                                        style={{ minWidth: "32px" }}
                                                        title="Deletar"
                                                        loading={carregandoDelecao}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}


                                

                                {agendamento.tipo === "INSTALAÇÃO" && (
                                    <>
                                        <div>
                                            <label style={{
                                                display: "block",
                                                marginBottom: "5px",
                                                fontWeight: "600",
                                                color: "#333",
                                                fontSize: "13px"
                                            }}>
                                                Depois
                                            </label>
                                        </div>
                                        <div>
                                            <FileUpload
                                                mode="basic"
                                                ref={fileUploadRef}
                                                accept="image/*,video/*"
                                                maxFileSize={10000000}
                                                onSelect={handleUploadArquivoDepois}
                                                auto
                                                chooseLabel="Adicionar"
                                                disabled={carregandoAnexo}
                                                emptyTemplate={
                                                    <p style={{
                                                        textAlign: "center",
                                                        padding: "20px",
                                                        color: "#999"
                                                    }}>
                                                        Arraste arquivos aqui ou clique para selecionar
                                                    </p>
                                                }
                                            />
                                        </div>

                                        {/* Lista de anexos */}
                                        {anexos && anexos.length > 0 && (
                                            <div style={{
                                                backgroundColor: "#f9f9f9",
                                                padding: "15px",
                                                borderRadius: "4px",
                                                marginTop: "15px"
                                            }}>
                                                <h4 style={{
                                                    color: "#333",
                                                    marginTop: 0,
                                                    marginBottom: "10px",
                                                    fontSize: "12px"
                                                }}>
                                                    ✅ Arquivos Anexados ({anexos.length})
                                                </h4>
                                                {anexos.filter(anexo => anexo.tipo === "FOTO_DEPOIS").map((anexo, index) => (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            padding: "8px",
                                                            backgroundColor: "#fff",
                                                            borderRadius: "4px",
                                                            marginBottom: "8px",
                                                            borderLeft: "4px solid #4CAF50",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "flex-start"
                                                        }}
                                                    >
                                                        <div style={{ flex: 1 }}>
                                                            <p style={{
                                                                margin: "0 0 5px 0",
                                                                fontSize: "12px",
                                                                fontWeight: "600",
                                                                color: "#333"
                                                            }}>
                                                                {anexo.tipo === "FOTO_ANTES" && "📷 Foto ANTES"}
                                                                {anexo.tipo === "FOTO_DEPOIS" && "📷 Foto DEPOIS"}
                                                                {anexo.tipo === "VIDEO_ANTES" && "🎥 Vídeo ANTES"}
                                                                {anexo.tipo === "VIDEO_DEPOIS" && "🎥 Vídeo DEPOIS"}
                                                                {anexo.tipo === "OUTRO" && "📎 Outro"}
                                                            </p>
                                                            <p style={{
                                                                margin: "0 0 5px 0",
                                                                fontSize: "11px",
                                                                color: "#666"
                                                            }}>
                                                                {anexo.nomeArquivo}
                                                            </p>
                                                            {anexo.observacao && (
                                                                <p style={{
                                                                    margin: "0",
                                                                    fontSize: "10px",
                                                                    color: "#999"
                                                                }}>
                                                                    <em>{anexo.observacao}</em>
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div style={{
                                                            display: "flex",
                                                            gap: "5px",
                                                            marginLeft: "10px"
                                                        }}>
                                                            <Button
                                                                icon="pi pi-eye"
                                                                className="p-button-rounded p-button-sm p-button-outlined"
                                                                onClick={() => visualizarAnexo(anexo)}
                                                                style={{ minWidth: "32px" }}
                                                                title="Visualizar"
                                                                loading={carregandoVisualizacao}
                                                            />
                                                            <Button
                                                                icon="pi pi-trash"
                                                                className="p-button-rounded p-button-sm p-button-outlined p-button-danger"
                                                                onClick={() => confirmDeleteAnexo(anexo)}
                                                                style={{ minWidth: "32px" }}
                                                                title="Deletar"
                                                                loading={carregandoDelecao}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <Divider />
                        </>
                    )}

                    {/* Seção de Informações de Monitoramento (se finalizado) */}
                    {agendamento.status === "CONCLUÍDO" && agendamento.monitoramentoAtivo && (
                        <>
                            <div style={{ marginBottom: "20px" }}>
                                <h3 style={{
                                    color: "#333",
                                    marginBottom: "10px",
                                    fontSize: "13px",
                                    fontWeight: "600"
                                }}>
                                    🔐 Informações de Monitoramento
                                </h3>
                                <div style={{
                                    backgroundColor: "#f5f5f5",
                                    padding: "15px",
                                    borderRadius: "4px",
                                    marginBottom: "10px"
                                }}>
                                    <p style={{
                                        fontSize: "12px",
                                        color: "#555",
                                        margin: "5px 0"
                                    }}>
                                        <strong>Usuário:</strong> {agendamento.usuarioMonitoramento}
                                    </p>
                                    <p style={{
                                        fontSize: "12px",
                                        color: "#555",
                                        margin: "5px 0"
                                    }}>
                                        <strong>Senha:</strong> {agendamento.senhaMonitoramento}
                                    </p>
                                </div>
                            </div>
                            <Divider />
                        </>
                    )}

                    {(agendamento.observacoesTecnico || agendamento.observacoesTecnico === "") && (
                        <p>
                            <strong>Observações:</strong> 
                            <InputTextarea
                                value={agendamento.observacoesTecnico}
                                placeholder="Digite suas observações (opcional)"
                                rows={3}
                                style={{ width: "100%", marginTop: "5px" }}
                            />
                        </p>
                    )}

                    {/* Botões de Ação */}
                    <div style={{
                        display: "flex",
                        gap: "10px",
                        justifyContent: "center",
                        marginTop: "20px",
                        flexWrap: "wrap"
                    }}>
                        {agendamento.status === "AGENDADO" && (
                            <Button
                                label="Iniciar"
                                icon="pi pi-play"
                                onClick={iniciarAgendamento}
                                severity="info"
                                style={{ padding: "10px 20px" }}
                            />
                        )}
                        
                        {(agendamento.status === "EM PROGRESSO" || agendamento.status === "CONFIRMADO") && (
                            <Button
                                label="Finalizar Agendamento"
                                icon="pi pi-check"
                                onClick={() => setMostrarModalFinalizacao(true)}
                                severity="success"
                                style={{ padding: "10px 20px" }}
                            />
                        )}
                    </div>
                </Card>
            </div>

            {/* Modal de Finalização */}
            <Dialog
                visible={mostrarModalFinalizacao}
                onHide={() => setMostrarModalFinalizacao(false)}
                header="Finalizar Agendamento"
                modal
                style={{ width: "90vw", maxWidth: "500px" }}
            >
                <div style={{ padding: "20px 0" }}>
                    <div style={{ marginBottom: "20px" }}>
                        <label style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: "600",
                            color: "#333",
                            fontSize: "13px"
                        }}>
                            ✅ Deixou o Monitoramento Funcionando?
                        </label>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px"
                        }}>
                            <InputSwitch
                                checked={monitoramentoAtivo}
                                onChange={(e) => {
                                    setMonitoramentoAtivo(e.value);
                                    if (!e.value) {
                                        setUsuarioMonitoramento("");
                                        setSenhaMonitoramento("");
                                    }
                                }}
                            />
                            <span style={{ fontSize: "13px" }}>{monitoramentoAtivo ? "Sim" : "Não"}</span>
                        </div>
                    </div>

                    {monitoramentoAtivo && (
                        <>
                            <div style={{ marginBottom: "15px" }}>
                                <label style={{
                                    display: "block",
                                    marginBottom: "5px",
                                    fontWeight: "600",
                                    color: "#333",
                                    fontSize: "13px"
                                }}>
                                    👤 Usuário do Monitoramento
                                </label>
                                <InputText
                                    value={usuarioMonitoramento}
                                    onChange={(e) => setUsuarioMonitoramento(e.target.value)}
                                    placeholder="Digite o usuário"
                                    style={{ width: "100%", fontSize: "13px" }}
                                />
                            </div>

                            <div style={{ marginBottom: "20px" }}>
                                <label style={{
                                    display: "block",
                                    marginBottom: "5px",
                                    fontWeight: "600",
                                    color: "#333",
                                    fontSize: "13px"
                                }}>
                                    🔑 Senha do Monitoramento
                                </label>
                                <InputText
                                    type="password"
                                    value={senhaMonitoramento}
                                    onChange={(e) => setSenhaMonitoramento(e.target.value)}
                                    placeholder="Digite a senha"
                                    style={{ width: "100%", fontSize: "13px" }}
                                />
                            </div>
                        </>
                    )}

                    <p style={{ fontSize: "13px", marginBottom: "10px" }}>
                        <strong>Observações:</strong> 
                        <InputTextarea
                            value={observacoesTecnico}
                            onChange={(e) => setObservacoesTecnico(e.target.value)}
                            placeholder="Digite suas observações (opcional)"
                            rows={3}
                            style={{ width: "100%", marginTop: "5px", fontSize: "13px" }}
                        />
                    </p>

                    <div style={{
                        display: "flex",
                        gap: "10px",
                        justifyContent: "flex-end"
                    }}>
                        <Button
                            label="Cancelar"
                            icon="pi pi-times"
                            onClick={() => setMostrarModalFinalizacao(false)}
                            severity="secondary"
                            disabled={carregandoFinalizacao}
                        />
                        <Button
                            label="Finalizar"
                            icon="pi pi-check"
                            onClick={finalizarAgendamento}
                            loading={carregandoFinalizacao}
                            severity="success"
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
